import React, { useEffect, useMemo, useState } from "react";
import { findMatchingReturnBaggageOption } from "../bridge/liveStageModels";
import FlightProgress from "../layout/FlightProgress";
import StickyBookingSummary from "../details/StickyBookingSummary";
import {
  buildMockExtrasBookingSummary,
  emptySelectionsForCatalog,
  getMockExtrasCatalog,
} from "./extrasMockData";
import "./ExtrasScreen.css";

function InclusionsSection({ title, inclusions, fare }) {
  return (
    <section className="fe2-ex-incl">
      <h2 className="fe2-ex-incl__title">{title}</h2>
      <ul className="fe2-ex-incl__list">
        {(inclusions || []).map((item) => (
          <li
            key={item.id}
            className={`fe2-ex-incl__item fe2-ex-incl__item--${item.tone}`}
          >
            <span className="fe2-ex-incl__mark" aria-hidden>
              {item.tone === "included" ? "✓" : "–"}
            </span>
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
      {fare ? (
        <p className="fe2-ex-incl__fare">
          Fare: <strong>{fare.family}</strong> · {fare.cabin}
        </p>
      ) : null}
    </section>
  );
}

function selectionSlotIds(slot) {
  if (Array.isArray(slot)) return slot;
  if (slot) return [slot];
  return [];
}

function PassengerTabs({ passengers, activeId, onChange, selections, optionsById }) {
  return (
    <div className="fe2-ex-tabs" role="tablist" aria-label="נוסעים">
      {passengers.map((pax) => {
        const sel = selections[pax.id] || {};
        const hasPaid = Object.values(sel).some((slot) =>
          selectionSlotIds(slot).some((optId) => {
            const opt = optionsById[optId];
            return opt && opt.priceUsd > 0;
          })
        );
        return (
          <button
            key={pax.id}
            type="button"
            role="tab"
            aria-selected={activeId === pax.id}
            className={
              activeId === pax.id
                ? "fe2-ex-tabs__btn fe2-ex-tabs__btn--active"
                : "fe2-ex-tabs__btn"
            }
            onClick={() => onChange(pax.id)}
          >
            <span className="fe2-ex-tabs__name fe2-ltr">{pax.name}</span>
            <span className="fe2-ex-tabs__meta">{pax.typeLabel}</span>
            {hasPaid ? <span className="fe2-ex-tabs__dot" aria-hidden /> : null}
          </button>
        );
      })}
    </div>
  );
}

function DirectionTabs({ directions, activeId, onChange }) {
  return (
    <div className="fe2-ex-dirs" role="tablist" aria-label="כיוון טיסה">
      {directions.map((dir) => {
        const route = String(dir.routeLabel || "").trim();
        return (
          <button
            key={dir.id}
            type="button"
            role="tab"
            aria-selected={activeId === dir.id}
            className={
              activeId === dir.id
                ? "fe2-ex-dirs__btn fe2-ex-dirs__btn--active"
                : "fe2-ex-dirs__btn"
            }
            onClick={() => onChange(dir.id)}
          >
            <strong>
              {dir.label}
              {route ? (
                <>
                  {" · "}
                  <span className="fe2-ltr">{route}</span>
                </>
              ) : null}
            </strong>
          </button>
        );
      })}
    </div>
  );
}

function DirectionContextLine({ direction, fallbackLabel }) {
  if (!direction) return fallbackLabel || null;
  const route = String(direction.routeLabel || "").trim();
  const date = String(direction.dateLabel || "").trim();
  return (
    <>
      {direction.label || fallbackLabel}
      {route ? (
        <>
          {" / "}
          <span className="fe2-ltr">{route}</span>
        </>
      ) : null}
      {date ? (
        <>
          {" / "}
          <span className="fe2-ltr">{date}</span>
        </>
      ) : null}
    </>
  );
}

function BaggageOptionCard({ option, selected, onSelect, onRemove, returnMatch, onAddReturn }) {
  const isIncluded = option.state === "included";
  const isUnavailable = option.state === "unavailable";
  const isSelected = selected && !isIncluded;
  const showPrice = !isIncluded && !isUnavailable;

  let action = null;
  if (isIncluded) {
    action = (
      <span className="fe2-ex-opt__pill fe2-ex-opt__pill--included">כלול ✓</span>
    );
  } else if (isUnavailable) {
    action = (
      <span className="fe2-ex-opt__pill fe2-ex-opt__pill--off">לא זמין</span>
    );
  } else if (isSelected) {
    action = (
      <div className="fe2-ex-opt__actions">
        <span className="fe2-ex-opt__pill fe2-ex-opt__pill--selected">✓ נוסף</span>
        <button type="button" className="fe2-ex-opt__link" onClick={onRemove}>
          הסר
        </button>
      </div>
    );
  } else {
    action = (
      <button type="button" className="fe2-ex-opt__add" onClick={onSelect}>
        הוסף
      </button>
    );
  }

  return (
    <article
      className={[
        "fe2-ex-opt",
        isIncluded ? "fe2-ex-opt--included" : "",
        isSelected ? "fe2-ex-opt--selected" : "",
        isUnavailable ? "fe2-ex-opt--unavailable" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="fe2-ex-opt__body">
        <div className="fe2-ex-opt__top">
          <h3 className="fe2-ex-opt__name">{option.name}</h3>
          {showPrice ? (
            option.priceUsdLabel && option.priceIlsLabel ? (
              <span className="fe2-ex-opt__price fe2-ltr">
                <span className="fe2-ex-opt__price-usd">{option.priceUsdLabel}</span>{" "}
                <span className="fe2-ex-opt__price-ils">({option.priceIlsLabel})</span>
              </span>
            ) : (
              <span className="fe2-ex-opt__price fe2-ltr">{option.priceLabel}</span>
            )
          ) : null}
        </div>
        <p className="fe2-ex-opt__meta">
          {option.weightLabel}
          {option.description ? ` · ${option.description}` : ""}
        </p>
      </div>
      <div className="fe2-ex-opt__side">{action}</div>
      {isSelected && returnMatch ? (
        <div className="fe2-ex-opt__return">
          <span className="fe2-ex-opt__return-q">להוסיף גם לחזור?</span>
          <button type="button" className="fe2-ex-opt__return-yes" onClick={() => onAddReturn(returnMatch)}>
            כן
          </button>
        </div>
      ) : null}
    </article>
  );
}

function SelectedExtrasPanel({
  passenger,
  directions,
  directionMode,
  selections,
  optionsById,
}) {
  const paxSel = selections[passenger.id] || {};
  const rows = [];
  const dirs =
    directionMode === "whole-trip"
      ? [{ id: "whole", label: "כל ההזמנה" }]
      : directions;

  let total = 0;
  let priced = false;
  for (const dir of dirs) {
    for (const optId of selectionSlotIds(paxSel[dir.id])) {
      const opt = optionsById[optId];
      if (!opt) continue;
      if (opt.priceUsd) {
        total += opt.priceUsd;
        priced = true;
      } else if (!opt.priceLabel) {
        continue;
      }
      rows.push({
        key: `${dir.id}-${opt.id}`,
        dirLabel: dir.label,
        name: opt.name,
        priceLabel: opt.priceLabel,
      });
    }
  }

  return (
    <section className="fe2-ex-sel">
      <h3 className="fe2-ex-sel__title">
        נבחר עבור <span className="fe2-ltr">{passenger.name}</span>
      </h3>
      {rows.length === 0 ? (
        <p className="fe2-ex-sel__empty">לא נבחרו תוספות.</p>
      ) : (
        <>
          <ul className="fe2-ex-sel__list">
            {rows.map((row) => (
              <li key={row.key} className="fe2-ex-sel__row">
                <span>
                  {row.dirLabel} · {row.name}
                </span>
                <span className="fe2-ex-sel__price fe2-ltr">{row.priceLabel}</span>
              </li>
            ))}
          </ul>
          {priced ? (
            <p className="fe2-ex-sel__total">
              סה״כ כבודה{" "}
              <strong className="fe2-ltr">${total}</strong>
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

function OtherExtrasSection({ title, items, selectedIds, onToggle }) {
  if (!items?.length) return null;
  return (
    <section className="fe2-ex-other">
      <h2 className="fe2-ex-other__title">{title}</h2>
      <div className="fe2-ex-other__grid">
        {items.map((item) => {
          const on = selectedIds.includes(item.id);
          return (
            <article
              key={item.id}
              className={on ? "fe2-ex-other__card fe2-ex-other__card--on" : "fe2-ex-other__card"}
            >
              <div>
                <h3 className="fe2-ex-other__name">{item.name}</h3>
                {item.description ? (
                  <p className="fe2-ex-other__desc">{item.description}</p>
                ) : null}
              </div>
              <div className="fe2-ex-other__side">
                <span className="fe2-ex-other__price fe2-ltr">{item.priceLabel}</span>
                <button
                  type="button"
                  className={on ? "fe2-ex-opt__link" : "fe2-ex-opt__add"}
                  onClick={() => onToggle(item.id)}
                >
                  {on ? "הסר" : "הוסף"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Extras / Baggage Design Preview — mock interaction only.
 * Sticky summary receives a derived ViewModel (incl. optional baggage breakdown).
 */
export default function ExtrasScreen({
  offer,
  search,
  catalog: catalogProp = null,
  onContinue,
  onBack,
  selections: selectionsProp = null,
  otherSelections: otherSelectionsProp = null,
  onSelectBaggage = null,
  onRemoveBaggage = null,
  onToggleOther = null,
}) {
  const catalog = catalogProp || getMockExtrasCatalog("none");
  const controlled = selectionsProp != null;
  const [activePaxId, setActivePaxId] = useState(catalog.passengers[0]?.id);
  const [activeDirId, setActiveDirId] = useState(
    catalog.directionMode === "whole-trip"
      ? "whole"
      : catalog.directions[0]?.id
  );
  useEffect(() => {
    const ids = (catalog.directions || []).map((dir) => dir.id);
    if (catalog.directionMode === "whole-trip") {
      if (activeDirId !== "whole") setActiveDirId("whole");
      return;
    }
    if (!ids.includes(activeDirId)) setActiveDirId(ids[0] || "outbound");
  }, [catalog.directionMode, catalog.directions, activeDirId]);
  const [localSelections, setLocalSelections] = useState(() =>
    emptySelectionsForCatalog(catalog)
  );
  const [localOtherSelections, setLocalOtherSelections] = useState(() => {
    const init = {};
    for (const p of catalog.passengers) init[p.id] = [];
    return { ...init, ...(catalog.initialOtherSelections || {}) };
  });
  const selections = controlled ? selectionsProp : localSelections;
  const otherSelections = controlled
    ? otherSelectionsProp || {}
    : localOtherSelections;

  const optionsById = useMemo(
    () => Object.fromEntries(catalog.baggageOptions.map((o) => [o.id, o])),
    [catalog]
  );

  const activePax =
    catalog.passengers.find((p) => p.id === activePaxId) || catalog.passengers[0];

  const dirKey =
    catalog.directionMode === "whole-trip" ? "whole" : activeDirId;
  const activeDirection =
    catalog.directions.find((dir) => dir.id === dirKey) || catalog.directions[0] || null;
  const visibleBaggageOptions = catalog.baggageOptions.filter((opt) => {
    if (!opt.directionId) return true;
    return opt.directionId === dirKey;
  });

  const selectedIds = selectionSlotIds(selections[activePax?.id]?.[dirKey]);
  const inboundSelectedIds = selectionSlotIds(selections[activePax?.id]?.inbound);

  const addMatchedReturn = (returnOpt) => {
    if (!returnOpt || !activePax) return;
    const dir = returnOpt.directionId || "inbound";
    if (controlled) {
      if (typeof onSelectBaggage === "function") {
        onSelectBaggage(activePax.id, dir, returnOpt.id);
      }
      return;
    }
    setLocalSelections((prev) => {
      const current = selectionSlotIds(prev[activePax.id]?.[dir]);
      if (current.includes(returnOpt.id)) return prev;
      return {
        ...prev,
        [activePax.id]: {
          ...(prev[activePax.id] || {}),
          [dir]: [...current, returnOpt.id],
        },
      };
    });
  };

  const derivedOffer = useMemo(() => {
    if (controlled) return offer;
    const base = offer?.bookingSummary || {};
    return {
      ...offer,
      bookingSummary: buildMockExtrasBookingSummary({
        baseSummary: base,
        catalog,
        selections,
        otherSelections,
      }),
    };
  }, [controlled, offer, catalog, selections, otherSelections]);

  const selectBag = (optionId) => {
    const opt = optionsById[optionId];
    if (!opt || opt.state === "included" || opt.state === "unavailable") return;
    if (controlled) {
      if (typeof onSelectBaggage === "function") {
        onSelectBaggage(activePax.id, dirKey, optionId);
      }
      return;
    }
    setLocalSelections((prev) => {
      const current = selectionSlotIds(prev[activePax.id]?.[dirKey]);
      if (current.includes(optionId)) return prev;
      let nextIds = current;
      if (opt.mutuallyExclusive === true) {
        nextIds = current.filter((id) => {
          const other = optionsById[id];
          if (!other || other.mutuallyExclusive !== true) return true;
          if (opt.exclusiveGroup || other.exclusiveGroup) {
            return other.exclusiveGroup !== opt.exclusiveGroup;
          }
          return false;
        });
      }
      return {
        ...prev,
        [activePax.id]: {
          ...(prev[activePax.id] || {}),
          [dirKey]: [...nextIds, optionId],
        },
      };
    });
  };

  const removeBag = (optionId) => {
    if (controlled) {
      if (typeof onRemoveBaggage === "function") {
        onRemoveBaggage(activePax.id, dirKey, optionId);
      }
      return;
    }
    setLocalSelections((prev) => ({
      ...prev,
      [activePax.id]: {
        ...(prev[activePax.id] || {}),
        [dirKey]: selectionSlotIds(prev[activePax.id]?.[dirKey]).filter((id) => id !== optionId),
      },
    }));
  };

  const toggleOther = (optId) => {
    if (controlled) {
      if (typeof onToggleOther === "function") onToggleOther(activePax.id, optId);
      return;
    }
    setLocalOtherSelections((prev) => {
      const cur = prev[activePax.id] || [];
      const next = cur.includes(optId)
        ? cur.filter((id) => id !== optId)
        : [...cur, optId];
      return { ...prev, [activePax.id]: next };
    });
  };

  return (
    <div className="fe2-ex-page">
      <header className="fe2-ex-head">
        <div className="fe2-ex-head__text">
          <h1 className="fe2-ex-head__title">{catalog.title}</h1>
          <p className="fe2-ex-head__subtitle">{catalog.subtitle}</p>
        </div>
        <button type="button" className="fe2-ex-head__back" onClick={onBack}>
          ← חזרה לפרטי נוסעים
        </button>
      </header>

      <FlightProgress activeStep="extras" />

      <div className="fe2-ex-page__grid">
        <div className="fe2-ex-page__main">
          <InclusionsSection
            title={catalog.inclusionsTitle}
            inclusions={catalog.inclusions}
            fare={catalog.fare}
          />

          {catalog.passengers.length > 1 ? (
            <PassengerTabs
              passengers={catalog.passengers}
              activeId={activePax.id}
              onChange={setActivePaxId}
              selections={selections}
              optionsById={optionsById}
            />
          ) : null}

          {catalog.directionMode === "per-segment" ? (
            <DirectionTabs
              directions={catalog.directions}
              activeId={activeDirId}
              onChange={setActiveDirId}
            />
          ) : (
            <p className="fe2-ex-whole">הקצאת כבודה חלה על כל ההזמנה</p>
          )}

          <section className="fe2-ex-bags" aria-label="אפשרויות כבודה">
            <div className="fe2-ex-bags__head">
              <h2 className="fe2-ex-bags__title">כבודה</h2>
              <p className="fe2-ex-bags__context">
                <DirectionContextLine
                  direction={activeDirection}
                  fallbackLabel={
                    catalog.directionMode === "whole-trip" ? "כל ההזמנה" : ""
                  }
                />
              </p>
            </div>
            <div className="fe2-ex-bags__list">
              {visibleBaggageOptions.map((opt) => {
                const selected = selectedIds.includes(opt.id);
                const returnMatch =
                  selected && dirKey === "outbound"
                    ? findMatchingReturnBaggageOption(opt, catalog.baggageOptions)
                    : null;
                const offerReturn =
                  returnMatch && !inboundSelectedIds.includes(returnMatch.id) ? returnMatch : null;
                return (
                  <BaggageOptionCard
                    key={opt.id}
                    option={opt}
                    selected={selected}
                    returnMatch={offerReturn}
                    onSelect={() => selectBag(opt.id)}
                    onRemove={() => removeBag(opt.id)}
                    onAddReturn={addMatchedReturn}
                  />
                );
              })}
            </div>
          </section>

          <SelectedExtrasPanel
            passenger={activePax}
            directions={catalog.directions}
            directionMode={catalog.directionMode}
            selections={selections}
            optionsById={optionsById}
          />

          <OtherExtrasSection
            title={catalog.otherExtrasTitle}
            items={catalog.otherExtras}
            selectedIds={otherSelections[activePax.id] || []}
            onToggle={toggleOther}
          />
        </div>

        <div className="fe2-ex-page__side">
          <StickyBookingSummary
            offer={derivedOffer}
            search={search}
            onContinue={onContinue}
            onBack={onBack}
            continueLabel="המשך לסיכום ההזמנה"
            backLabel="חזרה לפרטי נוסעים"
          />
        </div>
      </div>
    </div>
  );
}
